/**
 * Shared GraphQL API service for all Kestrix applications
 *
 * This module provides core GraphQL utilities for building API queries across
 * all Kestrix applications.
 *
 * SHARED UTILITIES:
 * - GQLPagedQuery: Core GraphQL query handler with pagination and caching
 * - getGraphQLEndpoint: Endpoint resolution utility
 * - getCurrentToken: JWT token reader utility
 *
 * USAGE:
 * import { GQLPagedQuery, getGraphQLEndpoint, getCurrentToken } from '@legacy-graphql-api';
 */

import axios from 'axios'

import { centralizedCache } from './CentralizedCache.js'
export { executeGraphQL } from './executeGraphQL.ts'

// =============================================================================
// SHARED UTILITIES
// =============================================================================

/**
 * Centralized JWT reader utility function
 * @returns {string} JWT token from localStorage
 */
export const getCurrentToken = () => {
  return (
    localStorage.getItem(import.meta.env.VITE_JWT_STORAGE_KEY || 'jwt_token') ||
    ''
  )
}

/**
 * Retrieves the GraphQL endpoint URL with environment and config fallback.
 *
 * This function implements a multi-tier approach to endpoint resolution:
 * 1. Environment variable (VITE_API_ENDPOINT) - highest priority
 * 2. Config.json file from current host - fallback for development
 * 3. Error thrown if neither source provides a valid endpoint
 *
 * The config.json approach allows for dynamic endpoint configuration without
 * rebuilding the application, useful for different deployment environments.
 *
 * @returns {Promise<string>} The GraphQL endpoint URL
 * @throws {Error} If unable to determine the GraphQL endpoint from any source
 *
 * @example
 * const endpoint = await getGraphQLEndpoint();
 * // Returns: 'https://api.kestrix.com/graphql'
 */
export const getGraphQLEndpoint = async () => {
  // First try to get from environment variable
  const envEndpoint = import.meta.env.VITE_API_ENDPOINT
  if (envEndpoint) {
    return envEndpoint
  }

  // Fallback to config.json approach (like the original Noodl app)
  const currentHost = window.location.hostname
  const currentPort = window.location.port
  const currentProtocol = window.location.protocol

  let currentPortStr = ''
  if (currentPort) currentPortStr = `:${currentPort}`

  const configUrl = `${currentProtocol}//${currentHost}${currentPortStr}/config.json`

  try {
    const response = await fetch(configUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch config: ${response.status}`)
    }

    const config = await response.json()
    return config.GraphQL_URL
  } catch (error) {
    console.error('Error fetching GraphQL endpoint:', error)
    throw new Error('Unable to determine GraphQL endpoint')
  }
}

/**
 * Fetches the GraphQL endpoint configuration.
 *
 * This function handles the dynamic configuration of the GraphQL endpoint.
 * If the endpoint is a config.json URL, it fetches the configuration file
 * and extracts the GraphQL_URL. Otherwise, it uses the URL directly.
 */
export const fetchConfig = async () => {
  try {
    const configUrl = await getGraphQLEndpoint()

    // If it's a config.json URL, fetch the config first
    if (configUrl.includes('config.json')) {
      const response = await fetch(configUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${getCurrentToken()}`
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`)
      }

      const config = await response.json()
      return config.GraphQL_URL
    }

    // Otherwise, use the URL directly as the GraphQL endpoint
    return configUrl
  } catch (error) {
    console.error('Error fetching GraphQL endpoint:', error)
    throw new Error('Unable to determine GraphQL endpoint')
  }
}

/**
 * GraphQL paged query handler for managing paginated API requests.
 *
 * This class centralizes GraphQL query execution with automatic pagination handling,
 * caching, and error management.
 *
 * Key Features:
 * - Automatic pagination: Fetches all pages in a single call via queryAllPages()
 * - Single page fetching: queryMoreSinglePage() for manual pagination (tables)
 * - Centralized caching: Integrates with CentralizedCache for performance
 *   - queryAllPages(): Caches complete datasets (maps)
 *   - queryMoreSinglePage(): Caches individual pages (tables)
 * - Error handling: Retry logic with page size reduction on timeouts
 * - Network abstraction: Single _executeNetworkRequest() method for all calls
 *
 * Used by: All API functions (fetchTableProperties, fetchMapMarkers, etc.)
 *
 * @param {string} url - The GraphQL endpoint URL
 * @param {string} queryStr - The GraphQL query string
 * @param {Object} variables - Query variables
 * @param {number|null} pageLimit - Number of items per page (null for no pagination)
 * @param {string} [functionName='query'] - Function name for error logging and debugging
 */
export class GQLPagedQuery {
  constructor(
    url,
    queryStr,
    variables = {},
    pageLimit = null,
    functionName = 'query'
  ) {
    this.url = url
    this.queryStr = queryStr
    this.variables = variables
    this.pageLimit = pageLimit
    this.functionName = functionName
    this.nextPage = 0
    this.cachedData = null
    this.isMore = true
    this.hasData = false
    this.pagedField = null
    this.dataFromCache = false // Track if data came from cache
    // Generate cache key without paging
    const cacheRequestBody = { query: queryStr, variables: { ...variables } }
    if (cacheRequestBody.variables.paging) {
      delete cacheRequestBody.variables.paging
    }
    this.cacheKey = centralizedCache.generateKey(cacheRequestBody)

    // Setup paging if required
    if (this.pageLimit) {
      // Look for the field name that is paged - updated regex to handle more complex query structures
      const regex = /(\w+)[\n\r\s]*\([^)]*paging:\s*\$paging[^)]*\)/g
      const matches = [...this.queryStr.matchAll(regex)]
      if (matches.length > 0) {
        this.pagedField = matches[0][1]
      } else {
        // Fallback: look for any field with paging parameter
        const fallbackRegex = /(\w+)\s*\([^)]*paging[^)]*\)/g
        const fallbackMatches = [...this.queryStr.matchAll(fallbackRegex)]
        if (fallbackMatches.length > 0) {
          this.pagedField = fallbackMatches[0][1]
        } else {
          // Manual fallback for our specific query structure
          if (this.queryStr.includes('properties(paging: $paging')) {
            this.pagedField = 'properties'
          }
        }
      }

      // Ensure $paging is declared; if a variable list exists, merge into it, else create one
      const hasPagingVar = /\$paging:\s*Paging!/.test(this.queryStr)
      if (!hasPagingVar) {
        // Case 1: query has an existing variable list e.g. query X($a: T, $b: U) { ... }
        if (/query\s+\w+\s*\([^)]*\)/.test(this.queryStr)) {
          this.queryStr = this.queryStr.replace(
            /(query\s+\w+\s*\()([^)]*)(\))/,
            (m, pre, vars, post) => {
              const cleaned = vars.trim()
              if (cleaned.length === 0) return `${pre}$paging: Paging!${post}`
              // If already has variables, append with comma
              return `${pre}${cleaned.replace(/\s+$/, '')}, $paging: Paging!${post}`
            }
          )
        } else {
          // Case 2: no variable list; extract name and wrap with ($paging: Paging!)
          const nameMatches = [
            ...this.queryStr.matchAll(/query\s+([a-zA-Z_]\w*)\s*[{(]/g)
          ]
          const queryName = nameMatches[0]?.[1] || 'Query'
          const bodyMatches = [
            ...this.queryStr.matchAll(/query\s+[a-zA-Z_]\w*\s*([{(](.|\n)*)$/g)
          ]
          const queryBody = bodyMatches[0]?.[1] || this.queryStr
          this.queryStr = `query ${queryName}($paging: Paging!) ${queryBody}`
        }
      }
    }
  }

  /**
   * Executes the GraphQL query and handles complete pagination flow.
   *
   * This method handles the entire pagination process, fetching all pages
   * automatically and caching the complete result. For non-paginated queries,
   * it executes once and caches the result.
   *
   * @returns {Promise<Record<string, unknown>>} Complete query result data
   * @throws {Error} If the query execution fails
   */
  async queryAllPages() {
    // Bail out if the query doesn't exist
    if (!this.queryStr) {
      throw new Error('No query specified')
    }

    // Check cache first for complete data (only for first page to avoid partial caching)
    if (this.nextPage === 0) {
      const cacheRequestBody = {
        query: this.queryStr,
        variables: { ...this.variables }
      }
      if (cacheRequestBody.variables.paging) {
        delete cacheRequestBody.variables.paging
      }
      const cacheKey = centralizedCache.generateKey(cacheRequestBody)
      const cachedData = centralizedCache.get(cacheKey)
      if (cachedData) {
        console.log('Loading complete data from centralized cache')
        this.cachedData = cachedData
        this.hasData = true
        this.isMore = false // Cached data is complete
        this.dataFromCache = true // Mark that data came from cache
        return this.cachedData
      }
    }

    // If no pagination, just execute once
    if (!this.pageLimit) {
      const requestBody = {
        query: this.queryStr,
        variables: this.variables
      }

      const responseData = await this._executeNetworkRequest(requestBody)
      const result = responseData.data

      this.cachedData = result
      this.hasData = true
      this.dataFromCache = false

      // Cache complete data for non-paginated queries
      this.cacheCompleteData()
      return result
    }

    // For paginated queries, load all pages
    let allData = null
    let hasMore = true

    while (hasMore) {
      // Set paging for this attempt
      const requestBody = {
        query: this.queryStr,
        variables: {
          ...this.variables,
          paging: {
            limit: this.pageLimit,
            page: this.nextPage
          }
        }
      }

      const responseData = await this._executeNetworkRequest(requestBody)
      const result = responseData.data

      // Update pagination state
      this.isMore = responseData.extensions?.page_info?.has_next_page || false

      if (this.pagedField) {
        const path = this.findKeyPath(result, this.pagedField)
        let newRows = result
        for (const key of path) {
          newRows = newRows[key]
        }

        // Determine if there are more pages based on returned data size
        // If we got fewer items than requested, we've reached the end
        this.isMore = newRows && newRows.length >= this.pageLimit
        this.nextPage += 1

        // If we already have data then append the new data to the existing data
        if (this.cachedData) {
          let existingRows = this.cachedData
          for (const key of path) {
            existingRows = existingRows[key]
          }

          if (newRows) {
            // Concatenate the new rows with the existing rows
            let obj = this.cachedData
            for (const key of path.slice(0, -1)) {
              obj = obj[key]
            }
            obj[path[path.length - 1]] = [...existingRows, ...newRows]
          }
        }
        // Otherwise cache just the data that we just received
        else {
          this.cachedData = result
        }
      } else {
        this.cachedData = result
      }

      allData = this.cachedData
      hasMore = this.isMore
    }

    // Cache complete data only when all pages are loaded
    this.cacheCompleteData()
    this.hasData = true
    this.dataFromCache = false

    return allData
  }

  /**
   * Generic retry logic with page size reduction for timeout errors.
   *
   * @param {Function} executeOnce - Function to execute (returns Promise)
   * @param {Object} options - Configuration options
   * @param {boolean} options.throwOnError - Whether to throw or return error tuple
   * @param {Function} options.onError - Custom error handler
   * @returns {Promise} Result from executeOnce or error handling result
   */
  async _executeWithRetry(executeOnce, options = {}) {
    const { throwOnError = false, onError = null } = options
    let attempts = 0
    const maxAttempts = 2 // initial try + one fallback attempt with reduced page size
    let lastError = null

    while (attempts < maxAttempts) {
      try {
        return await executeOnce()
      } catch (error) {
        lastError = error
        const message = (error?.message || '').toLowerCase()
        const status = error?.response?.status
        const isTimeout =
          message.includes('timeout') || error?.code === 'ECONNABORTED'
        const isGatewayTimeout = status === 504
        const canReduce = !!this.pageLimit && this.pageLimit > 10

        if ((isTimeout || isGatewayTimeout) && canReduce && attempts === 0) {
          // Reduce the page size and retry
          this.pageLimit = Math.max(10, Math.floor(this.pageLimit / 2))
          attempts += 1
          continue
        }

        // Handle error based on strategy
        if (onError) {
          return onError(error, this.nextPage, this.isMore)
        }

        if (throwOnError) {
          throw error
        } else {
          this.hasData = false
          return [false, error, this.nextPage, this.isMore]
        }
      }
    }

    // Exhausted attempts
    if (throwOnError) {
      throw lastError
    } else {
      this.hasData = false
      return [false, lastError, this.nextPage, this.isMore]
    }
  }

  /**
   * Private helper method to execute a single GraphQL request with common network logic.
   *
   * This method centralizes the axios post request logic including headers, timeout,
   * and error handling to eliminate duplication between queryAllPages and queryMoreSinglePage.
   *
   * @param {Object} requestBody - The GraphQL request body
   * @returns {Promise<Object>} The axios response data
   * @throws {Error} If the request fails
   * @private
   */
  async _executeNetworkRequest(requestBody) {
    // Get JWT token from localStorage (will be updated by auth context)
    const jwtToken = getCurrentToken()

    let response
    try {
      response = await axios.post(this.url, JSON.stringify(requestBody), {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${jwtToken}`
        },
        timeout: 30000
      })
    } catch (error) {
      // Never log the raw error or request body here: error.config.headers.Authorization
      // carries the live bearer token, and requestBody.variables can carry a plaintext
      // password (e.g. a Login mutation routed through this shared client).
      const safeMessage =
        error.response?.data?.message ||
        error.message ||
        'GraphQL request failed'
      if (error.response?.status === 400) {
        console.error(`400 Bad Request - ${safeMessage}`)
      }
      const wrappedError = new Error(safeMessage)
      if (error.code) {
        wrappedError.code = error.code
      }
      if (error.response?.status != null) {
        wrappedError.response = { status: error.response.status }
      }
      throw wrappedError
    }

    const responseData = response.data

    if (responseData.errors) {
      console.error('GraphQL errors:', responseData.errors)
      throw new Error(responseData.errors[0].message)
    }

    return responseData
  }

  /**
   * For Interactive Loading of Map Markers.
   * Used by: usePropertyMapData.js - fetchMapMarkers()
   * Manual pagination to load properties on the map one page at a time.
   * while the main queryAllPages() method handles default pagination in GQLPagedQuery.
   *
   * @returns {Promise<Array>} Query result array in old format
   * @returns {boolean} returns[0] - Whether the query was successful
   * @returns {Object|Error} returns[1] - Query data on success, error on failure
   * @returns {number} returns[2] - Next page number
   * @returns {boolean} returns[3] - Whether there are more pages available
   */
  async queryMoreSinglePage() {
    // Bail out if the query doesn't exist
    if (!this.queryStr) {
      throw new Error('No query specified')
    }

    const executeOnce = async () => {
      // Set paging for this attempt
      if (this.pageLimit) {
        this.variables.paging = {
          limit: this.pageLimit,
          page: this.nextPage
        }
      }

      const requestBody = {
        query: this.queryStr,
        variables: this.variables
      }

      // Check cache for this specific page (includes page number in cache key)
      const cacheKey = centralizedCache.generateKey(requestBody)
      const cachedData = centralizedCache.get(cacheKey)
      if (cachedData) {
        this.hasData = true
        this.nextPage += 1
        // Handle both old cache format (just data) and new format (data + totalCount)
        const data = cachedData.data || cachedData
        const totalCount = cachedData.totalCount || 0
        return [true, data, this.nextPage, this.isMore, totalCount]
      }

      const responseData = await this._executeNetworkRequest(requestBody)

      // Determine whether there is more data using explicit page_info from the API response
      this.isMore = responseData.extensions?.page_info?.has_next_page || false

      // Get total count from page_info extension
      const totalCount = responseData.extensions?.page_info?.total_count || 0

      if (this.pagedField) {
        const path = this.findKeyPath(responseData.data, this.pagedField)
        let newRows = responseData.data
        for (const key of path) {
          newRows = newRows[key]
        }
      }

      this.nextPage += 1

      this.hasData = true

      // Cache this page result for future requests (include totalCount in cache)
      const cacheData = {
        data: responseData.data,
        totalCount: totalCount
      }
      centralizedCache.set(cacheKey, cacheData)

      return [true, responseData.data, this.nextPage, this.isMore, totalCount]
    }

    return await this._executeWithRetry(executeOnce, {
      throwOnError: false,
      onError: (error, nextPage, isMore) => {
        this.hasData = false
        return [false, error, nextPage, isMore]
      }
    })
  }

  /**
   * Recursively finds the path to a key in a nested object.
   *
   * This method traverses an object to find the path (array of keys) to reach
   * a specific key, useful for accessing nested data in GraphQL responses.
   *
   * @param {Object} obj - The object to search in
   * @param {string} key - The key to find
   * @returns {Array<string>} Array of keys representing the path to the target key
   */
  findKeyPath(obj, key) {
    for (const prop in obj) {
      if (prop === key) {
        return [prop]
      }
      if (typeof obj[prop] === 'object') {
        const result = this.findKeyPath(obj[prop], key)
        if (result) {
          return [prop, ...result]
        }
      }
    }
    return []
  }

  /**
   * Caches the complete data only when all properties are loaded.
   * This method should be called after all pagination is complete.
   * Only caches if data was fetched from API, not from cache.
   */
  cacheCompleteData() {
    // Only cache if:
    // 1. We have data
    // 2. All pages are loaded (no more data)
    // 3. Data was fetched from API (not from cache)
    if (
      this.cachedData &&
      this.hasData &&
      !this.isMore &&
      !this.dataFromCache
    ) {
      const requestBody = {
        query: this.queryStr,
        variables: this.variables
      }

      // Remove paging from variables for cache key to ensure consistent caching
      const cacheRequestBody = {
        ...requestBody,
        variables: { ...requestBody.variables }
      }
      if (cacheRequestBody.variables.paging) {
        delete cacheRequestBody.variables.paging
      }

      const cacheKey = centralizedCache.generateKey(cacheRequestBody)
      centralizedCache.set(cacheKey, this.cachedData)
    } else if (this.dataFromCache) {
      // Data was already served from cache; nothing new to store.
    } else {
      console.log('Skipping cache - conditions not met')
    }
  }
}

// All model, annotation, and ASHP fetch functions have been moved to
// shared/3d-viewer-component/src/Server/ for better organization

// Default export for backward compatibility
export default GQLPagedQuery
